import React, { useState, useEffect, useRef } from 'react';
import { Users, Plus, Phone, Mail, MapPin, MessageSquare, Edit3, Trash2, Search } from 'lucide-react';
import { useT } from '../lib/useT';
import { apiFetch } from '../services/apiClient';
import { toast } from 'sonner'; // Bug 23: 添加 toast 导入

interface Contact {
  id: string; name: string; phone?: string; email?: string; company?: string;
  relationship?: string; notes?: string; interactionCount?: number; lastInteraction?: string;
}

export function ContactsPanel() {
  const t = useT();
  const isZh = t.langCode !== 'en';
  const ui = (zh: string, en: string) => isZh ? zh : en;
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '', company: '', relationship: 'friend', notes: '' });
  const [interactNote, setInteractNote] = useState('');
  // Bug 22: 搜索防抖定时器
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bug 24: 保存联系人的 loading 状态
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadContacts(); }, []);

  const loadContacts = async (query?: string) => {
    try {
      const url = query ? `/api/contacts?search=${encodeURIComponent(query)}` : '/api/contacts';
      const res = await apiFetch(url);
      if (res.ok) {
        const d = await res.json();
        setContacts(d.contacts || []);
      }
    } catch { toast.error(ui('加载联系人失败', 'Failed to load contacts')); } finally { setLoading(false); } // Bug 23: 添加 toast.error
  };

  const saveContact = async () => {
    if (!newContact.name.trim()) return;
    if (saving) return; // Bug 24: 防止重复提交
    setSaving(true);
    try {
      const res = await apiFetch('/api/contacts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newContact),
      });
      if (res.ok) {
        const c = await res.json();
        setContacts(prev => [...prev, c]);
        setNewContact({ name: '', phone: '', email: '', company: '', relationship: 'friend', notes: '' });
        setShowNew(false);
      } else {
        toast.error(ui('保存联系人失败', 'Failed to save contact')); // Bug 23: 添加 toast.error
      }
    } catch { toast.error(ui('网络错误', 'Network error')); } // Bug 23: 添加 toast.error
    finally { setSaving(false); } // Bug 24: 重置 saving 状态
  };

  const updateContact = async () => {
    if (!editing) return;
    try {
      const res = await apiFetch(`/api/contacts/${editing.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      if (res.ok) {
        const updated = await res.json();
        setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
        setEditing(null);
      } else {
        toast.error(ui('更新联系人失败', 'Failed to update contact')); // Bug 23: 添加 toast.error
      }
    } catch { toast.error(ui('网络错误', 'Network error')); } // Bug 23: 添加 toast.error
  };

  const deleteContact = async (id: string) => {
    // Bug 25: 添加删除确认弹窗
    if (!window.confirm(ui('确定要删除此联系人吗？', 'Are you sure you want to delete this contact?'))) return;
    try {
      await apiFetch(`/api/contacts/${id}`, { method: 'DELETE' });
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success(ui('已删除', 'Deleted'));
    } catch { toast.error(ui('删除联系人失败', 'Failed to delete contact')); } // Bug 23: 添加 toast.error
  };

  const recordInteraction = async (id: string, note: string) => {
    if (!note.trim()) return;
    try {
      const res = await apiFetch(`/api/contacts/${id}/interact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      if (res.ok) {
        const updated = await res.json();
        setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
        setInteractNote('');
      }
    } catch { toast.error(ui('记录互动失败', 'Failed to record interaction')); } // Bug 23: 添加 toast.error
  };

  if (loading) return <div className="p-6 text-white/40">加载中...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Users size={20} className="text-blue-400" />{ui('联系人', 'Contacts')}</h2>
        <button onClick={() => setShowNew(!showNew)} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm flex items-center gap-1">
          <Plus size={14} /> {ui('添加', 'Add')}
        </button>
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input
          value={search} onChange={e => {
            // Bug 22: 添加 300ms 防抖
            setSearch(e.target.value);
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            searchTimerRef.current = setTimeout(() => loadContacts(e.target.value), 300);
          }}
          placeholder={ui('搜索联系人...', 'Search contacts...')} className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 text-sm"
        />
      </div>

      {showNew && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <input value={newContact.name} onChange={e => setNewContact(p => ({ ...p, name: e.target.value }))} placeholder={ui('姓名 *', 'Name *')} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <input value={newContact.phone} onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))} placeholder={ui('电话', 'Phone')} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 text-sm" />
            <input value={newContact.email} onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))} placeholder={ui('邮箱', 'Email')} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 text-sm" />
          </div>
          <input value={newContact.company} onChange={e => setNewContact(p => ({ ...p, company: e.target.value }))} placeholder={ui('公司', 'Company')} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 text-sm" />
          <div className="flex gap-2">
            <button onClick={saveContact} disabled={!newContact.name.trim() || saving} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-lg text-sm">{saving ? ui('保存中...', 'Saving...') : ui('保存', 'Save')}</button>
            <button onClick={() => setShowNew(false)} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/60 rounded-lg text-sm">{ui('取消', 'Cancel')}</button>
          </div>
        </div>
      )}

      {contacts.length === 0 && <div className="text-white/30 text-center py-12">{ui('暂无联系人', 'No contacts yet')}</div>}

      <div className="space-y-2">
        {contacts.map(c => (
          <div key={c.id} className="bg-white/5 border border-white/10 rounded-xl p-4 group">
            {editing?.id === c.id ? (
              <div className="space-y-3">
                <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" />
                <input value={editing.phone || ''} onChange={e => setEditing({ ...editing, phone: e.target.value })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" />
                <input value={editing.email || ''} onChange={e => setEditing({ ...editing, email: e.target.value })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" />
                <div className="flex gap-2">
                  <button onClick={updateContact} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs">{ui('保存', 'Save')}</button>
                  <button onClick={() => setEditing(null)} className="px-3 py-1.5 bg-white/5 text-white/60 rounded-lg text-xs">{ui('取消', 'Cancel')}</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{c.name}</span>
                    {c.relationship && <span className="text-white/30 text-xs ml-2">{c.relationship}</span>}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setEditing(c)} className="p-1.5 text-white/30 hover:text-blue-400"><Edit3 size={12} /></button>
                    <button onClick={() => deleteContact(c.id)} className="p-1.5 text-white/30 hover:text-red-400"><Trash2 size={12} /></button>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                  {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                  {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                  {c.company && <span className="flex items-center gap-1"><MapPin size={10} />{c.company}</span>}
                </div>
                {c.lastInteraction && <div className="text-white/25 text-xs mt-1">{ui('最近互动', 'Last interaction')}: {new Date(c.lastInteraction).toLocaleDateString(isZh ? 'zh-CN' : undefined)}</div>}
                <div className="flex items-center gap-2 mt-2">
                  <input
                    value={interactNote} onChange={e => setInteractNote(e.target.value)}
                    placeholder={ui('添加互动记录...', 'Add interaction note...')} className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white/60 text-xs placeholder:text-white/20"
                    onKeyDown={e => { if (e.key === 'Enter') { recordInteraction(c.id, interactNote); } }}
                  />
                  <button onClick={() => recordInteraction(c.id, interactNote)} disabled={!interactNote.trim()} className="p-1.5 text-white/30 hover:text-green-400 disabled:opacity-30">
                    <MessageSquare size={12} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
